import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { WebappHeroVisual } from '../components/WebappHeroVisual';
import { WebappAppExperience } from '../components/WebappAppExperience';
import { WebappFeatures } from '../components/WebappFeatures';
import { WebappCases } from '../components/WebappCases';
import { webappAccents, webappSignals, webappExamples, webappSteps, webappOwnershipPoints, webappFaqs } from '../data/webapplicaties';
import { buildBreadcrumbList, buildFAQSchema, buildServiceSchema } from '../lib/structuredData';
const EASE = [0.23, 1, 0.32, 1];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const PAGE_PATH = '/webapplicatie-laten-maken';
export const WebapplicatiesPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-3162" data-mp="2qe0zfz2nfrjp">
      <SEO title="Webapplicatie laten maken in Noord-Holland | Noord Digital" description="Maatwerk webapplicaties voor het mkb in Noord-Holland. Klantportalen, dashboards, planning, calculatietools en koppelingen tussen je systemen. Software die past bij hoe jouw bedrijf werkt." keywords="webapplicatie laten maken, maatwerk software mkb, webapp laten bouwen, klantportaal laten maken, koppeling boekhouding crm, maatwerk software Noord-Holland" canonical={`https://noorddigital.nl${PAGE_PATH}`} jsonLd={[buildServiceSchema({
      name: 'Webapplicaties',
      description: 'Maatwerk webapplicaties voor het mkb in Noord-Holland: klantportalen, interne dashboards, planning- en calculatietools, samenwerkingstools en koppelingen tussen bestaande systemen.',
      path: PAGE_PATH,
      serviceType: 'Maatwerk softwareontwikkeling'
    }), buildBreadcrumbList([{
      name: 'Webapplicatie laten maken',
      path: PAGE_PATH
    }]), buildFAQSchema(webappFaqs)]} data-id="element-3163" data-mp="2uf2t80dsrbea" />
      <Header data-id="element-3164" data-mp="3ulgdgf82uin1" />

      <main data-id="element-3165" data-mp="3ukwtksyv9k6d">
        {/* Hero */}
        <section className="w-full bg-[#004AC7] pt-32 pb-20 md:pb-24" data-id="element-3166" data-mp="3qmh8wvq9cnw4">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3167" data-mp="3lml62ztar2i5">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-3168" data-mp="3lki7hre2w11t">
              <ol className="flex flex-wrap items-center gap-1.5 text-sm text-white/70" data-id="element-3169" data-mp="3gmgpl9se27rx">
                <li data-id="element-3170" data-mp="3bpgfmnsf4kvj">
                  <a href="/" className="hover:text-white transition-colors duration-200" data-id="element-3171" data-mp="3box7tzwhfcv8">
                    Home
                  </a>
                </li>
                <li aria-hidden="true" data-id="element-3172" data-mp="19xk6fktq3n3x">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-white/40" data-id="element-3173" data-mp="19wiiwgvit85b" />
                </li>
                <li className="font-medium text-white" aria-current="page" data-id="element-3174" data-mp="15vyldc71c2in">
                  Webapplicatie laten maken
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-id="element-3175" data-mp="2srdbsqgz34t3">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} data-id="element-3176" data-mp="2xid5znypj8rw">
                <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-3177" data-mp="29taqomrjps0k">
                  Webapplicatie laten maken
                </h1>
                <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-4" data-id="element-3178" data-mp="2dtkuptvs28cs">
                  Software op maat die je werk makkelijker maakt.
                </p>
                <p className="text-white/75 leading-relaxed mb-8 max-w-xl" data-id="element-3179" data-mp="3p99hol9177dl">
                  Geen standaardpakket waar je je bedrijf omheen moet bouwen en
                  geen no-code oplossing die bij de eerste uitzondering
                  vastloopt. Wij programmeren een webapplicatie die precies past
                  bij hoe jouw bedrijf werkt, voor het mkb in{' '}
                  <a href="/werkgebied/alkmaar" className="underline underline-offset-4 decoration-white/50 hover:decoration-white transition-colors duration-200" data-id="element-3180" data-mp="5nsim2fa7e6j">
                    Alkmaar
                  </a>{' '}
                  en de rest van Noord-Holland Noord.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-3181" data-mp="amvmhjjizecx">
                  <a href="/#contact" className="inline-flex items-center justify-center py-4 px-8 bg-white text-[#004AC7] font-semibold rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 group" data-id="element-3182" data-mp="ak9cld95wmrj">
                    Plan een kennismaking
                    <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3183" data-mp="yczm7ftletyn" />
                  </a>
                  <a href="#app-ervaring" className="inline-flex items-center justify-center py-4 px-8 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors duration-200" data-id="element-3184" data-mp="1cywqw922ndft">
                    Bekijk wat de app kan
                  </a>
                </div>
              </motion.div>

              <div className="lg:col-span-5" data-id="element-3185" data-mp="17ysjr9n1zv29">
                <WebappHeroVisual data-id="element-3186" data-mp="181n2mwqf57tk" />
              </div>
            </div>
          </div>
        </section>

        {/* Signalen */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3187" data-mp="yym0f74oc0v6">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3188" data-mp="z06wj16mzrv5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3189" data-mp="kbkeustjeo0r">
              <motion.div {...fadeUp} className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start" data-id="element-3190" data-mp="pa40st84rzt4">
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-3191" data-mp="baci4f6o5v8s">
                  Wanneer is een webapp de oplossing?
                </h2>
                <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3192" data-mp="2hxjvzpim0ujz">
                  Niet elk bedrijf heeft maatwerk nodig. Maar herken je jezelf
                  in twee of meer van deze signalen, dan kost de huidige manier
                  van werken je waarschijnlijk meer tijd dan je denkt.
                </p>
              </motion.div>

              <ul className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 border-t border-gray-200" data-id="element-3193" data-mp="247gd98ixviza">
                {webappSignals.map((signal, index) => {
                const Icon = signal.icon;
                return <li key={signal.title} className="flex gap-5 border-b border-gray-200 py-6" data-id="element-3194" data-mp="2vy4dmicsgkbw" data-mpi={index}>
                      <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${webappAccents[index % webappAccents.length].chip}`} data-id="element-3195" data-mp="2rtwo4iyib0cm">
                        <Icon className="h-5 w-5" aria-hidden="true" data-id="element-3196" data-mp="1ggugweiarn54" />
                      </div>
                      <div data-id="element-3197" data-mp="1bh84266ds8wu">
                        <h3 className="font-semibold text-[#111827] text-lg mb-1" data-id="element-3198" data-mp="1bfws3j2s7uun">
                          {signal.title}
                        </h3>
                        <p className="text-[#6B7280] leading-relaxed" data-id="element-3199" data-mp="3s3o7lx65m0ol">
                          {signal.body}
                        </p>
                      </div>
                    </li>;
              })}
              </ul>
            </div>
          </div>
        </section>

        <WebappAppExperience data-id="element-3200" data-mp="4sonf20792hd" />

        <WebappFeatures data-id="element-3201" data-mp="9qgdrrl74gjg" />

        {/* Wat we bouwen */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3202" data-mp="9s16dbny9peo">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3203" data-mp="enqbduiqnyid">
            <motion.div {...fadeUp} className="max-w-2xl mb-12" data-id="element-3204" data-mp="jnuulsb177ca">
              <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-3205" data-mp="nppsemtr72lb">
                Wat we bouwen
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3206" data-mp="2lg9a6kq3rzpj">
                Elke applicatie is anders, maar de meeste vragen vallen in een
                van deze soorten. Vaak is het een combinatie.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3207" data-mp="26iq0rj7uj95j">
              {webappExamples.map((example, index) => {
              const Icon = example.icon;
              return <div key={example.title} className="flex flex-col rounded-2xl border border-gray-200 bg-white p-7" data-id="element-3208" data-mp="2fccocm1wh63n" data-mpi={index}>
                    <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${webappAccents[index % webappAccents.length].chip}`} data-id="element-3209" data-mp="2bdwxg72p7dky">
                      <Icon className="h-6 w-6" aria-hidden="true" data-id="element-3210" data-mp="1io6rilb7zhzx" />
                    </div>
                    <h3 className="font-bold text-[#111827] text-xl mb-2" data-id="element-3211" data-mp="1wnpku7zdgos6">
                      {example.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed" data-id="element-3212" data-mp="1rpo61z8zbbmi">
                      {example.body}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </section>

        {/* Werkwijze */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3213" data-mp="3bj7o2689u4a3">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3214" data-mp="3bltsvy3s25om">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3215" data-mp="3fmnj4y4ozwko">
              <motion.div {...fadeUp} className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start" data-id="element-3216" data-mp="31muvjbogpwgx">
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-3217" data-mp="36lfnyoxh3but">
                  Zo werken we
                </h2>
                <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3218" data-mp="3ucl1uyjuru2h">
                  Je ziet vroeg wat je krijgt en stuurt bij terwijl het nog
                  makkelijk kan. Zo voorkom je dat er na maanden bouwen iets
                  staat dat niet past.
                </p>
              </motion.div>

              <ol className="lg:col-span-8 relative" data-id="element-3219" data-mp="h3ozyyuwefgb">
                {webappSteps.map((step, index) => <li key={step.title} className="relative flex gap-6 pb-10 last:pb-0" data-id="element-3220" data-mp="h1vs5g8jtp6q" data-mpi={index}>
                    {index < webappSteps.length - 1 && <span className="absolute left-5 top-11 bottom-0 w-px bg-gray-200" aria-hidden="true" data-id="element-3221" data-mp="1e04q19rj1de3" />}
                    <span className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 border-[#004AC7] bg-white text-sm font-bold text-[#004AC7]" data-id="element-3222" data-mp="1izhhy8x99anj">
                      {index + 1}
                    </span>
                    <div className="pt-1.5" data-id="element-3223" data-mp="1a0e0tahnf5bo">
                      <h3 className="font-bold text-[#111827] text-xl mb-2" data-id="element-3224" data-mp="v8vfqy9xmklg">
                        {step.title}
                      </h3>
                      <p className="text-[#6B7280] leading-relaxed" data-id="element-3225" data-mp="101eptl9euxli">
                        {step.body}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </div>
          </div>
        </section>

        {/* Van jou, niet van een platform */}
        <section className="w-full bg-[#1B2B44] py-20 md:py-24" data-id="element-3226" data-mp="cb15yi0zc520">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3227" data-mp="h9vodiv1brll">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3228" data-mp="3tsyd2av02gzd">
              <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-3229" data-mp="3tv1cfli1lmgr">
                <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-3230" data-mp="3ovohsekxjb3m">
                  Van jou, niet van een platform
                </h2>
                <p className="text-white/85 text-lg leading-relaxed mb-6" data-id="element-3231" data-mp="2n6ohfwvfceo">
                  Bij een SaaS-pakket huur je software die voor duizenden
                  bedrijven tegelijk gemaakt is. Stopt het pakket, verandert de
                  prijs of verdwijnt een functie, dan heb je daar niets over te
                  zeggen. Een maatwerk applicatie is gebouwd voor jouw bedrijf.
                </p>
                <a href="/hosting" className="inline-flex items-center gap-2 text-white font-semibold underline underline-offset-4 decoration-white/40 hover:decoration-white transition-colors duration-200" data-id="element-3232" data-mp="lcv7qbrd14e7">
                  Bekijk onze hosting
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3233" data-mp="vdfcvpi02t1p" />
                </a>
              </motion.div>

              <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8" data-id="element-3234" data-mp="12cy1ddzsykut">
                {webappOwnershipPoints.map(point => {
                const Icon = point.icon;
                return <li key={point.title} data-id="element-3235" data-mp="1cb405mqbbavg">
                      <Icon className="h-6 w-6 text-white mb-3" aria-hidden="true" data-id="element-3236" data-mp="1h3x2y528094w" />
                      <h3 className="font-semibold text-white text-lg mb-1.5" data-id="element-3237" data-mp="1l5s499k5imio">
                        {point.title}
                      </h3>
                      <p className="text-white/80 leading-relaxed" data-id="element-3238" data-mp="1q2sbvownfxbw">
                        {point.body}
                      </p>
                    </li>;
              })}
              </ul>
            </div>
          </div>
        </section>

        {/* Praktijkvoorbeelden */}
        <WebappCases data-id="element-3239" data-mp="qgryurg0856" />

        {/* FAQ */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3240" data-mp="ri5r6zuf8ql">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[900px]" data-id="element-3241" data-mp="5pb97s9rhon6">
            <motion.h2 {...fadeUp} className="font-bold text-[#111827] text-3xl md:text-4xl mb-10 text-center" data-id="element-3242" data-mp="2iz1hm3twijj7">
              Veelgestelde vragen over webapplicaties
            </motion.h2>

            <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white" data-id="element-3243" data-mp="2a5y6i26qairq">
              {webappFaqs.map((faq, index) => {
              const isOpen = openFaq === index;
              const panelId = `webapp-faq-${index}`;
              return <div key={faq.question} data-id="element-3244" data-mp="31vjhj8iwlpqj" data-mpi={index}>
                    <h3 data-id="element-3245" data-mp="2xvslapalyhem">
                      <button type="button" onClick={() => setOpenFaq(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-[#111827] hover:text-[#004AC7] transition-colors duration-200" data-id="element-3246" data-mp="2xxddi89g9o9b">
                        {faq.question}
                        <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 text-[#004AC7] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-3247" data-mp="1cipi9zfcevm1" />
                      </button>
                    </h3>
                    <AnimatePresence initial={false} data-id="element-3248" data-mp="356dqffp8dfhr">
                      {isOpen && <motion.div id={panelId} initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2,
                    ease: EASE
                  }} className="overflow-hidden" data-id="element-3249" data-mp="2repfvgumqz2w">
                          <p className="px-6 pb-6 text-[#4B5563] leading-relaxed" data-id="element-3250" data-mp="3a4ozho7pblyx">
                            {faq.answer}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </section>

        {/* Afsluitende CTA */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3251" data-mp="1w5dgre5om6ie">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3252" data-mp="1w3smzqr6yrll">
            <motion.div {...fadeUp} className="rounded-2xl bg-[#1B2B44] px-6 py-10 sm:px-8 sm:py-12 md:px-14 md:py-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8" data-id="element-3253" data-mp="1r5r3x5trmsa4">
              <div className="max-w-2xl" data-id="element-3254" data-mp="208a5lmlteyus">
                <h2 className="font-bold text-white text-2xl md:text-3xl mb-3" data-id="element-3255" data-mp="205e6huy8e44r">
                  Loop je vast in Excel, losse tools of handwerk?
                </h2>
                <p className="text-white/80 text-lg leading-relaxed" data-id="element-3256" data-mp="18hcgstmkjyel">
                  Vertel ons hoe het werk nu loopt. In een eerste gesprek kijken
                  we samen of een webapplicatie de oplossing is, of dat iets
                  eenvoudigers volstaat. Je zit tot niets verplicht.
                </p>
              </div>
              <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-white text-[#004AC7] font-semibold py-4 px-8 rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 flex-shrink-0 whitespace-nowrap" data-id="element-3257" data-mp="tosf7xf2hyz9">
                Plan een kennismaking
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3258" data-mp="2trjq4uzm13wg" />
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-3259" data-mp="33jh94nwv105q" />
    </div>;
};